import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { Text } from '@dealstack/frontend/shared/components/fundament/Text/Text';
import React, { memo, useState } from 'react';
import styled, { css } from 'styled-components';

interface Props {
	className?: string;
	initialOpened?: boolean;
	title: string;
	children: React.ReactNode | React.ReactNode[];
	preventRenderWhenHidden?: boolean;
}

const Expandable = ({
	title,
	children,
	preventRenderWhenHidden = false,
	initialOpened = false,
	className,
}: Props) => {
	const [isOpened, setIsOpened] = useState<boolean>(initialOpened);

	return (
		<div className={className}>
			<Header isOpened={isOpened} onClick={() => setIsOpened(!isOpened)}>
				<Text variant="h5" bold>
					{title}
				</Text>
				<Icon variant="chevron-down" size="lg" />
			</Header>

			<Content isOpened={isOpened}>
				{preventRenderWhenHidden && !isOpened ? null : children}
			</Content>
		</div>
	);
};

const Header = styled.div<{ isOpened: boolean }>`
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 1rem;
	cursor: pointer;

	svg {
		transition: transform 0.3s;
		color: ${({ theme }) => theme.v2.color.neutral[700]};
		${({ isOpened }) => isOpened && 'transform: rotate(180deg)'};
	}

	${({ isOpened, theme }) =>
		isOpened && `border-bottom: 1px solid ${theme.v2.color.neutral[300]};`};
`;

export const Content = styled.div<{ isOpened: boolean }>`
	padding: 0 1rem;
	overflow: ${({ isOpened }) => !isOpened && `hidden !important`};
	max-height: 0;
	transition:
		max-height 0.1s,
		padding 0.3s;

	${({ isOpened }) =>
		isOpened &&
		css`
			max-height: 100vh;
			overflow: auto;
			padding: 1.5rem 1rem;
		`};
`;

const Memoized = memo(Expandable);
const Styled = styled(Memoized)`
	border-radius: 0.5rem;
	background-color: ${({ theme }) => theme.v2.color.neutral[200]};
`;

export { Styled as Expandable };
